import { InferType } from 'yup'
import { Form, Formik, type FormikProps } from 'formik'
import FormikTextInput from '@/shared/forms/components/FormikTextInput'
import React from 'react'
import { FormProps } from '@/shared/components/hi/types'
import { IHIFormValidator } from '@/shared/components/hi/IHIFormValidator'
import Link from 'next/link'
import { GETTING_IHI_LINK } from '@/lib/constants'
import {
  DuplicateAccountNotice,
  QuizInput,
} from '@/shared/components/hi/MedicareForm'
import { motion, useReducedMotion } from 'framer-motion'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

export interface IHIFormType extends InferType<typeof IHIFormValidator> {}

type Props = FormProps<IHIFormType> & {
  variant?: 'default' | 'quiz' | 'profile'
  duplicateAccountError?: boolean
  onClearServerError?: () => void
  onClickLogin?: () => void
}

export function IHIForm({
  onVerify,
  state,
  onInputChanged,
  children,
  variant = 'default',
  duplicateAccountError = false,
  onClearServerError,
  onClickLogin,
}: Props) {
  return (
    <Formik
      initialValues={state.values}
      onSubmit={async (values, { setSubmitting }) => {
        await onVerify(values)
        setSubmitting(false)
      }}
      validationSchema={IHIFormValidator}
      validateOnMount={true}
      initialTouched={state.touched}
    >
      {(props) =>
        variant === 'quiz' ? (
          <QuizIHIForm
            duplicateAccountError={duplicateAccountError}
            formik={props}
            onClearServerError={onClearServerError}
            onClickLogin={onClickLogin}
            onInputChanged={onInputChanged}
          >
            {children?.(props)}
          </QuizIHIForm>
        ) : variant === 'profile' ? (
          <ProfileIHIForm
            formik={props}
            onInputChanged={onInputChanged}
            state={state}
          >
            {children?.(props)}
          </ProfileIHIForm>
        ) : (
          <DefaultIHIForm
            formik={props}
            onInputChanged={onInputChanged}
            state={state}
          >
            {children?.(props)}
          </DefaultIHIForm>
        )
      }
    </Formik>
  )
}

function ProfileIHIForm({
  children,
  formik,
  onInputChanged,
  state,
}: {
  children?: React.ReactNode
  formik: FormikProps<IHIFormType>
  onInputChanged: FormProps<IHIFormType>['onInputChanged']
  state: FormProps<IHIFormType>['state']
}) {
  const { errors, isSubmitting, touched, values } = formik

  return (
    <Form>
      <QuizInput
        autoComplete="ihi-number"
        disabled={isSubmitting}
        error={touched.ihi && errors.ihi ? String(errors.ihi) : undefined}
        label="IHI number"
        name="ihi"
        onBlur={() => {
          void formik.setFieldTouched('ihi', true)
          onInputChanged(values, { ...touched, ihi: true })
        }}
        onChange={(value) => {
          void formik.setFieldValue('ihi', value)
          onInputChanged({ ihi: value }, { ...touched, ihi: true })
        }}
        placeholder="16 digits, starting with 8003"
        value={values.ihi ?? ''}
      />

      {state.verified && (
        <div className="mt-4 flex items-center gap-2 text-[13.5px] font-semibold leading-[18px] text-[#1F8A70]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#1F8A70]" />
          Verified
        </div>
      )}

      {children}
    </Form>
  )
}

function DefaultIHIForm({
  formik,
  onInputChanged,
  state,
  children,
}: {
  formik: FormikProps<IHIFormType>
  onInputChanged: FormProps<IHIFormType>['onInputChanged']
  state: FormProps<IHIFormType>['state']
  children?: React.ReactNode
}) {
  return (
    <Form>
      <p className="mb-3 text-sm text-gray-600">
        You can get your IHI number by logging into{' '}
        <Link
          href="https://my.gov.au/"
          target="_blank"
          className="cursor-pointer text-blue-600 hover:underline"
        >
          MyGov
        </Link>{' '}
        and requesting with a valid passport and visa. See instructions{' '}
        <Link
          href={GETTING_IHI_LINK}
          target="_blank"
          className="cursor-pointer text-blue-600 hover:underline"
        >
          here
        </Link>
        .
      </p>

      <FormikTextInput
        name="ihi"
        label="IHI Number"
        autocomplete="ihi-number"
        size="full"
        placeholder="80036XXXXXXXXXXX"
        onChange={(
          event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          formik.handleChange(event)
          onInputChanged({ ihi: event.target.value }, { ihi: true })
        }}
        onBlur={(
          event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          formik.handleBlur(event)
          onInputChanged(formik.values, { ihi: true })
        }}
        verified={state.verified?.ihi === formik.values.ihi}
      />
      {children}
    </Form>
  )
}

function QuizIHIForm({
  formik,
  onInputChanged,
  duplicateAccountError,
  onClearServerError,
  onClickLogin,
  children,
}: {
  formik: FormikProps<IHIFormType>
  onInputChanged: FormProps<IHIFormType>['onInputChanged']
  duplicateAccountError: boolean
  onClearServerError?: () => void
  onClickLogin?: () => void
  children?: React.ReactNode
}) {
  const reduceMotion = useReducedMotion()
  const { values, touched, errors, isSubmitting } = formik

  return (
    <Form>
      <motion.div
        animate="visible"
        className="mb-0 mt-5 flex flex-col gap-2 sm:mb-0 sm:mt-[22px]"
        initial={reduceMotion ? false : 'hidden'}
        variants={{
          hidden: {},
          visible: {
            transition: { delayChildren: 0.05, staggerChildren: 0.065 },
          },
        }}
      >
        <QuizIHIReveal reduceMotion={reduceMotion}>
          <QuizInput
            autoComplete="ihi-number"
            disabled={isSubmitting}
            error={touched.ihi && errors.ihi ? String(errors.ihi) : undefined}
            hasServerError={duplicateAccountError}
            label="IHI number"
            name="ihi"
            onBlur={() => {
              void formik.setFieldTouched('ihi', true)
              onInputChanged(values, { ...touched, ihi: true })
            }}
            onChange={(value) => {
              onClearServerError?.()
              void formik.setFieldValue('ihi', value)
              onInputChanged({ ihi: value }, { ...touched, ihi: true })
            }}
            placeholder="16 digits, starting with 8003"
            value={values.ihi ?? ''}
          />
          <DuplicateAccountNotice
            identifierLabel="IHI number"
            onClickLogin={onClickLogin}
            show={duplicateAccountError}
          />
        </QuizIHIReveal>

        <QuizIHIReveal reduceMotion={reduceMotion}>
          <p className="text-[13px] leading-[18px] text-[#82828B]">
            You&apos;ll find it in your Medicare online account or the my health
            app.
          </p>
        </QuizIHIReveal>

        <QuizIHIReveal reduceMotion={reduceMotion}>
          <Link
            className="text-[13.5px] font-semibold leading-[18px] text-[#2563EB] transition-colors duration-300 hover:text-[#1D4ED8]"
            href={GETTING_IHI_LINK}
            target="_blank"
          >
            Don&apos;t have one? See how to request an IHI — including as a
            visitor to Australia →
          </Link>
        </QuizIHIReveal>
      </motion.div>

      {children}
    </Form>
  )
}

function QuizIHIReveal({
  children,
  reduceMotion,
}: {
  children: React.ReactNode
  reduceMotion: boolean | null
}) {
  return (
    <motion.div
      variants={
        reduceMotion
          ? undefined
          : {
              hidden: { opacity: 0, y: 9 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: softMotionDuration.panel,
                  ease: softEditorialEase,
                },
              },
            }
      }
    >
      {children}
    </motion.div>
  )
}
